import React, { useState, useEffect } from "react";
import { getAPIURL, getVendorID, getVendorUsers } from "../../services/helper";
import http from "../../services/http";
import DataTable from "react-data-table-component";
import {
  Spinner,
  Container,
  Card,
  Input,
  Button,
  Row,
  Col,
  CardBody,
  CardTitle,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import {
  FaShoppingCart,
  FaUser,
  FaDollarSign,
  FaReceipt,
  FaCashRegister,
} from "react-icons/fa";
import { useDispatch } from "react-redux";
import { openView } from "../../ReduxToolkit/Reducers/viewReducer";
import classnames from "classnames";

const SalesReportByUser = ({ backTo }) => {
  const api_url = getAPIURL();
  const vendorId = getVendorID();
  const dispatch = useDispatch();
  const [data, setData] = useState({ summary: {}, details: [] });
  const [activeTab, setActiveTab] = useState("summary");
  const [loading, setLoading] = useState(true);
  const [billerId, setBillerId] = useState("");
  const [users, setUsers] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1))
      .toISOString()
      .split("T")[0], // Last month
    end: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    fetchVendorUsers();
    fetchSalesReport();
  }, []);

  useEffect(() => {
    fetchSalesReport();
  }, [billerId, dateRange.start, dateRange.end]);

  const fetchVendorUsers = async () => {
    try {
      const users = await getVendorUsers(vendorId);
      setUsers(users);
    } catch (error) {
      console.error("Error fetching vendor users:", error);
    }
  };

  const fetchSalesReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ vendor_id: vendorId });
      if (billerId) params.append("biller_id", billerId);
      if (dateRange.start) params.append("start_date", dateRange.start);
      if (dateRange.end) params.append("end_date", dateRange.end);

      const { data } = await http.get(
        `${api_url}/reports/sales/vendor?${params.toString()}`
      );
      setData(data);
    } catch (error) {
      console.error("Error fetching sales report:", error);
    }
    setLoading(false);
  };

  const columns = [
    { name: "User", selector: (row) => row.biller_name, sortable: true },
    { name: "Order ID", selector: (row) => row.order_id, sortable: true },
    { name: "Date", selector: (row) => row.order_date, sortable: true },
    {
      name: "Total Amount",
      selector: (row) => `$${parseFloat(row.order_total || 0).toFixed(2)}`,
      sortable: true,
    },
    {
      name: "Paid",
      selector: (row) => `$${parseFloat(row.amount_paid || 0).toFixed(2)}`,
      sortable: true,
    },
    {
      name: "Change Given",
      selector: (row) => `$${parseFloat(row.change_given || 0).toFixed(2)}`,
      sortable: true,
    },
    {
      name: "Tax Collected",
      selector: (row) => `$${parseFloat(row.tax_collected || 0).toFixed(2)}`,
      sortable: true,
    },
  ];

  return (
    <div className="page-body">
      <Container className="mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          {backTo && (
            <Button color="primary" onClick={() => dispatch(openView(backTo))}>
              Back
            </Button>
          )}
          <h2 className="text-center flex-grow-1">Sales Report by User</h2>
          <div></div>
        </div>



        {/* Tabs */}
        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "summary" })}
              onClick={() => setActiveTab("summary")}
              style={{ cursor: "pointer" }}
            >
              Summary
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "listing" })}
              onClick={() => setActiveTab("listing")}
              style={{ cursor: "pointer" }}
            >
              Listing
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          <TabPane tabId="summary">
            {loading ? (
              <Spinner color="primary" />
            ) : (
              <>
                <Row className="mb-4">
                  <Col md="3">
                    <Card className="p-3 shadow-sm text-center">
                      <CardBody>
                        <FaUser size={30} className="text-primary mb-2" />
                        <CardTitle tag="h5">Total Users</CardTitle>
                        <h4>{users.length || 0}</h4>
                      </CardBody>
                    </Card>
                  </Col>

                  <Col md="3">
                    <Card className="p-3 shadow-sm text-center">
                      <CardBody>
                        <FaShoppingCart size={30} className="text-success mb-2" />
                        <CardTitle tag="h5">Total Sales</CardTitle>
                        <h4>{data.summary.total_sales || 0}</h4>
                      </CardBody>
                    </Card>
                  </Col>

                  <Col md="3">
                    <Card className="p-3 shadow-sm text-center">
                      <CardBody>
                        <FaDollarSign size={30} className="text-warning mb-2" />
                        <CardTitle tag="h5">Total Revenue</CardTitle>
                        <h4>${data.summary.total_amount?.toFixed(2) || "0.00"}</h4>
                      </CardBody>
                    </Card>
                  </Col>

                  <Col md="3">
                    <Card className="p-3 shadow-sm text-center">
                      <CardBody>
                        <FaReceipt size={30} className="text-danger mb-2" />
                        <CardTitle tag="h5">Total Tax</CardTitle>
                        <h4>${data.summary.total_tax?.toFixed(2) || "0.00"}</h4>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                <Row className="mb-4">
                  <Col md="3">
                    <Card className="p-3 shadow-sm text-center">
                      <CardBody>
                        <FaCashRegister size={30} className="text-info mb-2" />
                        <CardTitle tag="h5">Total Change Given</CardTitle>
                        <h4>
                          ${data.summary.total_change_given?.toFixed(2) || "0.00"}
                        </h4>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </>
            )}
          </TabPane>

          <TabPane tabId="listing">
            {/* Filters */}
            <div className="d-flex mb-3 gap-2">
              <Input
                type="select"
                value={billerId}
                onChange={(e) => setBillerId(e.target.value)}
                placeholder="Select User"
              >
                <option value="">All Users</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </Input>
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) =>
                  setDateRange({ ...dateRange, start: e.target.value })
                }
                placeholder="Start Date"
              />
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) =>
                  setDateRange({ ...dateRange, end: e.target.value })
                }
                placeholder="End Date"
              />
              <Button color="secondary" onClick={() => {
                setBillerId("");
                setDateRange({
                  start: new Date(new Date().setMonth(new Date().getMonth() - 1))
                    .toISOString()
                    .split("T")[0],
                  end: new Date().toISOString().split("T")[0],
                });
              }}>
                Reset
              </Button>
            </div>
            
            {loading ? (
              <Spinner color="primary" />
            ) : (
              <Card className="p-3 shadow-sm">
                <h4 className="mb-3">Transactions</h4>
                <DataTable
                  columns={columns}
                  data={data.details.flatMap((user) => 
                    (user.transactions || []).map(transaction => ({
                      ...transaction,
                      biller_name: user.biller_name
                    }))
                  )}
                  pagination
                />
              </Card>
            )}
          </TabPane>
        </TabContent>

      </Container>
    </div>
  );
};

export default SalesReportByUser;
